import React, { useCallback, useEffect, useRef, useState } from 'react';
import {createPortal} from 'react-dom';

function Tooltip({
  children,
  className,
  bg,
  size,
  position,
  trigger,
  triggerClassName = '',
  onClick,
}) {

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const triggerRef = useRef(null);
  const [portalStyle, setPortalStyle] = useState(null);

  const tooltipWidth = () => {
    switch (size) {
      case 'lg':
        return 384;
      case 'md':
        return 320;
      case 'sm':
        return 224;
      default:
        return 240;
    }
  };

  const updatePortalPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const width = Math.min(tooltipWidth(), window.innerWidth - 32);
    const centeredLeft = rect.left + rect.width / 2 - width / 2;
    const left = Math.max(16, Math.min(centeredLeft, window.innerWidth - width - 16));

    switch (position) {
      case 'right':
        setPortalStyle({left: Math.min(rect.right + 8, window.innerWidth - width - 16), top: rect.top + rect.height / 2, width, transform: 'translateY(-50%)'});
        break;
      case 'left':
        setPortalStyle({left: Math.max(16, rect.left - width - 8), top: rect.top + rect.height / 2, width, transform: 'translateY(-50%)'});
        break;
      case 'bottom':
        setPortalStyle({left, top: rect.bottom + 8, width});
        break;
      default:
        setPortalStyle({left, bottom: window.innerHeight - rect.top + 8, width});
    }
  }, [position, size]);

  useEffect(() => {
    if (!tooltipOpen) return undefined;

    updatePortalPosition();
    window.addEventListener('resize', updatePortalPosition);
    window.addEventListener('scroll', updatePortalPosition, true);

    return () => {
      window.removeEventListener('resize', updatePortalPosition);
      window.removeEventListener('scroll', updatePortalPosition, true);
    };
  }, [tooltipOpen, updatePortalPosition]);

  const sizeClasses = (size) => {
    switch (size) {
      case 'lg':
        return 'w-96 max-w-[calc(100vw-2rem)] px-3 py-2';
      case 'md':
        return 'w-80 max-w-[calc(100vw-2rem)] px-3 py-2';
      case 'sm':
        return 'w-56 max-w-[calc(100vw-2rem)] px-3 py-2';
      default:
        return 'px-3 py-2';
    }
  };

  const colorClasses = (bg) => {
    switch (bg) {
      case 'light':
        return 'bg-white text-gray-600 border-gray-200';
      case 'dark':
        return 'bg-gray-800 text-gray-100 border-gray-700/60';
      default:
        return 'text-gray-600 bg-white dark:bg-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700/60';
    }
  };

  return (
    <div
      ref={triggerRef}
      className={`relative ${className}`}
      onMouseEnter={() => setTooltipOpen(true)}
      onMouseLeave={() => setTooltipOpen(false)}
      onFocus={() => setTooltipOpen(true)}
      onBlur={() => setTooltipOpen(false)}
    >
      <button
        className={`${trigger ? "inline-flex" : "block"} ${triggerClassName}`}
        aria-haspopup="true"
        aria-expanded={tooltipOpen}
        onClick={(event) => {
          event.preventDefault();
          onClick?.();
        }}
      >
        {trigger ?? (
          <svg className="shrink-0 fill-current opacity-40" width="16" height="16" viewBox="0 0 16 16">
            <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm1 12H7V7h2v5zM8 6c-.6 0-1-.4-1-1s.4-1 1-1s1 .4 1 1-.4 1-1 1z"></path>
          </svg>
        )}
      </button>
      {tooltipOpen && portalStyle && createPortal(<div className="pointer-events-none fixed z-[1000]" style={portalStyle}>
        <div className={`whitespace-normal break-words rounded-lg border overflow-hidden shadow-lg ${sizeClasses(size)} ${colorClasses(bg)}`}>
          {children}
        </div>
      </div>, document.body)}
    </div>
  );
}

export default Tooltip;
